import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "../../components/Modal";
import ShuffleHeader from "./components/ShuffleHeader";
import ShuffleSelections from "./components/ShuffleSelections";
import styles from "./BetShuffle.module.scss";
import { shuffleSelections } from "../../lib/api";
import {
  filterOutBetslipSelections,
  getDateFormat,
  isObjEmpty,
} from "../../lib";
import Filters from "../../components/Filters";
import { convertOdds } from "../../lib/odds";
import SelectionsHeader from "../../experiments/BubbleJourney/components/SelectionsHeader";

// create wrapper component for if the user is part of the bubble journey experiment
const BubbleJourneyBetShuffle = ({ children, isLoading }) => {
  return (
    <Modal loading={isLoading}>
      <div className={styles.betShuffle}>
        <SelectionsHeader location="betShuffle">{children}</SelectionsHeader>
      </div>
    </Modal>
  );
};

const DefaultBetShuffle = ({ children, isLoading }) => {
  return (
    <Modal loading={isLoading}>
      <div className={styles.betShuffle}>{children}</div>
    </Modal>
  );
};

const BetShuffle = () => {
  const betslip = useSelector((state) => state.betslip);
  const { filters, ppConfig } = useSelector((state) => state.filter);
  const { date, fixtures, markets, odds } = filters;
  const { marketGroups } = ppConfig;
  const { activeView, previousView, experiments } = useSelector(
    (state) => state.view
  );
  const { bubbleUI } = experiments;
  const [selections, setSelections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shuffled, setShuffled] = useState(false);
  const countRef = React.useRef(0);

  // Only gets called if we get less than 5 selections and we should make another request for more shuffle selections
  const getMoreShuffleSelections = async (params) => {
    let res;
    let filteredSelections;
    let apiCallsCount = 0;
    const maxNumberOfApiCalls = 5;

    // Only call shuffleSelections again whilst filtered selections length < 5
    do {
      res = await shuffleSelections(countRef.current, params);
      countRef.current += 1;

      if (res.code === 0) {
        filteredSelections = filterOutBetslipSelections(res.acca, betslip);
        apiCallsCount++;

        // Break out of loop here to prevent an infinite loop
        if (apiCallsCount === maxNumberOfApiCalls) {
          // Allow maximum number of 5 x api calls - can update the number if needed.
          break;
        }
      }
    } while (filteredSelections.length < 5);
    setSelections(filteredSelections);
  };

  const getShuffleSelections = async (count = null) => {
    const storedFixtures =
      date.length === 1
        ? fixtures[date[0]]
        : [...fixtures.today, ...fixtures.tomorrow];

    const numberOfMarketsAvailable = marketGroups?.reduce(
      (sum, marketGroup) => sum + marketGroup.markets.length,
      0
    );

    const numberOfFixturesAvailable = parseInt(
      localStorage.getItem("numberOfAvailableFixtures")
    );

    const handleDateParam = date.length === 1 ? getDateFormat(date[0]) : null;

    const handleMarketsParam =
      markets.length > 0 && markets.length !== numberOfMarketsAvailable
        ? markets.join()
        : null;

    const { minOdds, maxOdds } = !isObjEmpty(odds)
      ? odds
      : {
          minOdds: null,
          maxOdds: null,
          isDecimal: false,
        };

    const oddsParams = {
      minOdds: convertOdds({
        odds: bubbleUI && minOdds ? minOdds.toFixed(1) : minOdds,
        type: "min",
      }),
      maxOdds: convertOdds({
        odds: bubbleUI && maxOdds ? maxOdds.toFixed(1) : maxOdds,
        type: "max",
      }),
    };

    const handleFixturesParam =
      storedFixtures.length > 0 &&
      storedFixtures.length !== numberOfFixturesAvailable
        ? storedFixtures.join()
        : null;

    const params = {
      date: handleDateParam,
      markets: handleMarketsParam,
      fixtures: handleFixturesParam,
      minOdds: oddsParams.minOdds?.toString() || null,
      maxOdds: oddsParams.maxOdds?.toString() || null,
    };

    const countParam = count !== null ? count : countRef.current;

    const res = await shuffleSelections(countParam, params);
    countRef.current += 1;

    if (res.code === 0) {
      // Filter out betslip selections here
      const filteredSelections = filterOutBetslipSelections(res.acca, betslip);

      // Make sure selections is populated only if the filtered out betslip selections array length is greater than 0
      // and greater than 4
      if (filteredSelections.length > 4) {
        setSelections(filteredSelections);
      } else {
        // If we are left with the unlikely scenario of having less than 5 shuffle selections displaying because
        // of selections in the betslip then we should make another call to get more shuffle selections.
        // If we get less than 5 selections and we should make another request for more shuffle selections
        // via getMoreShuffleSelections
        getMoreShuffleSelections(params);
      }

      // Add delay to setIsLoading being set to false when data is available so we see the loading animation
      setTimeout(() => {
        setIsLoading(false);
      }, 750);
    } else {
      // Make sure we set setIsLoading back to false if the API call fails so we don't stay in a loading loop
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === "betShuffle" && previousView !== "betShuffle") {
      getShuffleSelections();
    } else {
      countRef.current = 0;
    }

    // only set loading state if the active view user returns to is the default home view
    // otherwise they are within a filter and we need to maintain the old selections
    if (activeView === "") {
      setIsLoading(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => {
    if (activeView === "betShuffle") {
      setIsLoading(true);
      getShuffleSelections(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, fixtures, markets, odds]);

  if (activeView !== "betShuffle") return null;

  if (bubbleUI) {
    return (
      <BubbleJourneyBetShuffle isLoading={isLoading}>
        <ShuffleHeader
          selections={selections}
          getShuffleSelections={() => getShuffleSelections()}
          isLoading={isLoading}
          setIsLoading={() => setIsLoading(true)}
          shuffled={shuffled}
          setShuffled={(e) => setShuffled(e)}
        />
        <ShuffleSelections selections={selections} isLoading={isLoading} />
      </BubbleJourneyBetShuffle>
    );
  }

  return (
    <DefaultBetShuffle isLoading={isLoading}>
      <Filters />
      <ShuffleHeader
        selections={selections}
        getShuffleSelections={() => getShuffleSelections()}
        isLoading={isLoading}
        setIsLoading={() => setIsLoading(true)}
        shuffled={shuffled}
        setShuffled={(e) => setShuffled(e)}
      />
      <ShuffleSelections selections={selections} isLoading={isLoading} />
    </DefaultBetShuffle>
  );
};

export default BetShuffle;
